import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
    Accordion,
    Button,
    CustomAnchor,
    FloatingPanel,
    InputText,
    Modal,
    PopoverInfo,
    PopoverThreeDots,
    Toggle,
} from "../../../components";
import { getDocGroupsAPI, applyDocGroupsAPI } from "../../../common/services";
import { useToaster } from "../../../common/contexts/toast";
import { errorMessageOf } from "../../../utils";

// A group is typed `filter` | `attribute`; filter groups drive search/facets,
// attribute groups become doc metadata. Both hold values in one panel.
const GROUP_TYPES = [
    { id: "filter", displayName: "Filter" },
    { id: "attribute", displayName: "Attribute" },
];
const DEFAULT_TYPE = "filter";

// A blank group in "create" state, with one empty value ready to fill.
const makeNewGroup = () => ({
    id: uuidv4(),
    displayName: "",
    type: DEFAULT_TYPE,
    isNew: true,
    isEditingName: true,
    values: [{ id: uuidv4(), displayName: "", isNew: true }],
});

const hasBlankValue = (group) => (group.values || []).some((v) => !(v.displayName || "").trim());

const ManageFiltersPanel = ({
    visible = false,
    title = "Manage filters and attributes",
    onClose,
    onApplied,
}) => {
    const { showToaster } = useToaster();

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [applying, setApplying] = useState(false);
    const [newValueId, setNewValueId] = useState(null); // id of the just-added value (to autofocus)
    const [pendingDelete, setPendingDelete] = useState(null); // { groupId, valueId?, type } awaiting confirmation; no valueId = whole group

    // Snapshot of the seeded state, kept to diff against on APPLY.
    const seedRef = useRef([]);

    // Load the org's taxonomy each time the panel opens.
    useEffect(() => {
        if (!visible) return;

        let cancelled = false;
        setLoading(true);
        setNewValueId(null);
        getDocGroupsAPI()
            .then((res) => {
                if (cancelled) return;
                // System groups (Projects / Project objects / File type) are
                // provided by the backend and cannot be edited here.
                const editableGroups = (res?.data?.items || []).filter((group) => !group.system);
                const seeded = seedGroups(editableGroups);
                // Set the snapshot first: React 16 renders synchronously on setState
                // outside event handlers, and the APPLY diff reads it in that render.
                seedRef.current = seeded;
                setGroups(seeded.length ? seeded : [makeNewGroup()]);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error("[manage-groups] failed to load taxonomy", error);
                seedRef.current = [];
                setGroups([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [visible]);

    const addGroup = useCallback(() => {
        setGroups((prev) => [...prev, makeNewGroup()]);
    }, []);

    const renameGroup = useCallback((groupId) => {
        setGroups((prev) =>
            prev.map((grp) => (grp.id === groupId ? { ...grp, isEditingName: true } : grp))
        );
    }, []);

    const deleteGroup = useCallback((groupId) => {
        setGroups((prev) => {
            const next = prev.filter((grp) => grp.id !== groupId);
            return next.length ? next : [makeNewGroup()];
        });
    }, []);

    const setGroupType = useCallback((groupId, type) => {
        setGroups((prev) =>
            prev.map((grp) => (grp.id === groupId ? { ...grp, type } : grp))
        );
    }, []);

    const addValue = useCallback((groupId) => {
        const valueId = uuidv4();
        let added = false;
        setGroups((prev) =>
            prev.map((grp) => {
                if (grp.id !== groupId || hasBlankValue(grp)) return grp;
                added = true;
                return { ...grp, values: [...grp.values, { id: valueId, displayName: "", isNew: true }] };
            })
        );
        if (added) setNewValueId(valueId);
    }, []);

    const deleteValue = useCallback((groupId, valueId) => {
        setGroups((prev) =>
            prev.map((grp) =>
                grp.id === groupId
                    ? { ...grp, values: grp.values.filter((v) => v.id !== valueId) }
                    : grp
            )
        );
    }, []);

    // A saved value may be on files, so removing it asks first; a new one goes straight away.
    const requestDeleteValue = useCallback((group, value) => {
        if (value.isNew) {
            deleteValue(group.id, value.id);
            return;
        }
        setPendingDelete({ groupId: group.id, valueId: value.id, type: group.type });
    }, [deleteValue]);

    // Only saved groups have the menu, so deleting one always asks first.
    const requestDeleteGroup = useCallback((group) => {
        setPendingDelete({ groupId: group.id, type: group.type });
    }, []);

    const confirmDelete = useCallback(() => {
        const { groupId, valueId } = pendingDelete;
        if (valueId) deleteValue(groupId, valueId);
        else deleteGroup(groupId);
        setPendingDelete(null);
    }, [deleteGroup, deleteValue, pendingDelete]);

    const editGroupName = useCallback((groupId, value) => {
        setGroups((prev) =>
            prev.map((grp) => (grp.id === groupId ? { ...grp, displayName: value } : grp))
        );
    }, []);

    const editValue = useCallback((groupId, valueId, value) => {
        setGroups((prev) =>
            prev.map((grp) =>
                grp.id === groupId
                    ? {
                          ...grp,
                          values: grp.values.map((v) =>
                              v.id === valueId ? { ...v, displayName: value } : v
                          ),
                      }
                    : grp
            )
        );
    }, []);

    // The whole panel commits as ONE batch of taxonomy actions.
    const actions = useMemo(() => buildActions(groups, seedRef.current), [groups]);
    const canApply = actions.length > 0;

    const handleApply = useCallback(async () => {
        if (!canApply || applying) return;

        setApplying(true);
        try {
            await applyDocGroupsAPI({ actions });
            showToaster && showToaster("manage-groups-success", "Changes saved.", true);
            onApplied && onApplied();
            onClose && onClose();
        } catch (error) {
            showToaster && showToaster(
                "manage-groups-error",
                errorMessageOf(error, "Couldn't save the changes."),
                false
            );
        } finally {
            setApplying(false);
        }
    }, [canApply, applying, actions, onApplied, onClose, showToaster]);

    return (
        <FloatingPanel
            id="manage-filters"
            open={visible}
            title={title}
            onClose={onClose}
            withoutHeaderIcon
        >
            <div className="manage-filters-panel-body">
                {groups.map((grp) => (
                    <GroupCard
                        key={grp.id}
                        group={grp}
                        newValueId={newValueId}
                        onRename={renameGroup}
                        onDeleteGroup={requestDeleteGroup}
                        onChangeType={setGroupType}
                        onAddValue={addValue}
                        onDeleteValue={requestDeleteValue}
                        onEditName={editGroupName}
                        onEditValue={editValue}
                    />
                ))}
                {loading && <div className="page-loader" />}
            </div>

            <div className="manage-filters-panel-footer">
                <CustomAnchor onClick={addGroup} className="manage-filters-create-group">
                    Create new group
                </CustomAnchor>
                <div className="manage-filters-footer-actions">
                    <Button name="CANCEL" type="primary" theme="red" onClick={onClose} />
                    <Button
                        name="APPLY"
                        type="secondary"
                        theme="red"
                        disable={!canApply || applying}
                        loading={applying}
                        onClick={handleApply}
                    />
                </div>
            </div>

            <DeleteConfirmModal
                type={pendingDelete?.type}
                isGroup={!!pendingDelete && !pendingDelete.valueId}
                visible={!!pendingDelete}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
            />
        </FloatingPanel>
    );
};

// Confirms removing a filter/attribute value or a whole group, worded by the group's type.
const DeleteConfirmModal = ({ type, isGroup, visible, onClose, onConfirm }) => {
    const isAttribute = type === "attribute";
    const noun = isAttribute ? "attribute" : "filter";

    return (
        <Modal
            visible={visible}
            type="surprised"
            size="large"
            title={isGroup ? "Delete group" : isAttribute ? "Delete attribute" : "Delete filter"}
            extraMessage={
                isGroup ? (
                    <>
                        You are about to delete {isAttribute ? "an" : "a"} {noun} group.
                        <br /><br />
                        All its {noun}s will be removed from all affiliated files.
                    </>
                ) : (
                    <>
                        You are about to delete {isAttribute ? "an" : "a"} {noun}.
                        <br /><br />
                        The {noun} will be removed from all affiliated files.
                    </>
                )
            }
            onClose={onClose}
            onClickCancel={onClose}
            onClickConfirm={onConfirm}
            cancelButtonName="CANCEL"
            confirmButtonName="DELETE"
            modalClass="delete-filter-value-modal"
        />
    );
};

const GroupCard = ({
    group,
    newValueId,
    onRename,
    onDeleteGroup,
    onChangeType,
    onAddValue,
    onDeleteValue,
    onEditName,
    onEditValue,
}) => {
    const showNameInput = group.isNew || group.isEditingName;

    const body = (
        <>
            {showNameInput && (
                <InputText
                    label="Group name"
                    required
                    info={GROUP_NAME_INFO}
                    infoPlacement="right"
                    placeholder="Type"
                    value={group.displayName}
                    className="manage-filters-group-name"
                    onChange={(value) => onEditName(group.id, value)}
                />
            )}

            <div className="manage-filters-filters-grid">
                {group.values.map((value) => (
                    <InputText
                        key={value.id}
                        hideLabel
                        // Focus (and scroll into view) the just-added value.
                        autoFocus={value.id === newValueId}
                        placeholder={group.isNew ? "Type the filter name" : "< Value >"}
                        value={value.displayName}
                        iconSrc="/images/icon/trash.svg"
                        onIconClick={() => onDeleteValue(group, value)}
                        onChange={(next) => onEditValue(group.id, value.id, next)}
                    />
                ))}
            </div>

            <CustomAnchor
                onClick={() => onAddValue(group.id)}
                disabled={hasBlankValue(group)}
                className="manage-filters-create-filter"
            >
                Create new value
            </CustomAnchor>
        </>
    );

    const typeToggle = (
        <Toggle
            className="manage-filters-type-toggle"
            data={GROUP_TYPES}
            value={group.type}
            onChange={(type) => onChangeType(group.id, type)}
            hideLabel
            background="grey"
        />
    );

    if (group.isNew) {
        return (
            <div className="manage-filters-new-group">
                <div className="manage-filters-new-group-header">{typeToggle}</div>
                {body}
            </div>
        );
    }

    const expanded = group.isEditingName;
    return (
        <Accordion
            // Accordion seeds its open state on mount only; re-key so renaming a collapsed group remounts it open and reveals the name input.
            key={`acc-${expanded}`}
            title={group.displayName || "New group"}
            open={expanded}
            collapsible
            hideSettings
            RightHandSideComponent={
                <span className="manage-filters-group-actions" onClick={(e) => e.stopPropagation()}>
                    {typeToggle}
                    <PopoverInfo message={ATTRIBUTES_INFO} placement="top" />
                    <GroupMenu group={group} onRename={onRename} onDeleteGroup={onDeleteGroup} />
                </span>
            }
        >
            {body}
        </Accordion>
    );
};

const GroupMenu = ({ group, onRename, onDeleteGroup }) => {
    const options = useMemo(
        () => [
            {
                id: "rename",
                label: "Rename the group",
                src: "/images/icon/pencil.svg",
                onClick: () => onRename(group.id),
            },
            {
                id: "delete",
                src: "/images/icon/trash.svg",
                className: "manage-filters-delete-item",
                onClick: () => onDeleteGroup(group),
                label: <span className="manage-filters-delete-title">Delete the group</span>,
            },
        ],
        [group, onRename, onDeleteGroup]
    );

    // Stop the click from bubbling to the Accordion head (which toggles expand).
    return (
        <span className="manage-filters-group-menu" onClick={(e) => e.stopPropagation()}>
            <PopoverThreeDots
                placement="bottom"
                align="end"
                options={options}
                className="manage-filters-group-popover"
            />
        </span>
    );
};

const GROUP_NAME_INFO = 'Define the group name by the context of its values, e.g. "Roles" or "Document type"';
const ATTRIBUTES_INFO = "Attributes are additional info which not function as filters";

// Seed the editable model from GET /docs/groups
// ({ id, displayName, type, order, values: [{ id, displayName, order }] }).
const seedGroups = (items = []) =>
    items.map((group) => ({
        id: group.id,
        displayName: group.displayName || "",
        type: group.type || DEFAULT_TYPE,
        isNew: false,
        isEditingName: false,
        values: (group.values || []).map((v) => ({
            id: v.id,
            displayName: v.displayName || "",
            isNew: false,
        })),
    }));

/**
 * Diff the edited model against the seed and emit the backend's batch action
 * list. New groups/values carry a client `ref`/`groupRef` so the backend can
 * resolve in-batch dependencies; existing ones use real ids. Group ops carry
 * the group's `type` (filter | attribute); values are typed by their group.
 */
const buildActions = (groups, seed) => {
    const seedById = new Map(seed.map((s) => [s.id, s]));
    const currentIds = new Set(groups.map((grp) => grp.id));
    const actions = [];

    // groups present in the snapshot but gone now => deleted
    seed.forEach((s) => {
        if (!currentIds.has(s.id)) actions.push({ op: "delete_group", groupId: s.id });
    });

    groups.forEach((grp, groupIndex) => {
        const name = (grp.displayName || "").trim();
        const type = grp.type || DEFAULT_TYPE;
        const seedGroup = seedById.get(grp.id);

        if (!seedGroup) {
            // Brand-new group — only meaningful once it has a name.
            if (!name) return;
            actions.push({ op: "create_group", ref: grp.id, displayName: name, type, order: groupIndex });
            grp.values.forEach((v, valueIndex) => {
                const value = (v.displayName || "").trim();
                if (!value) return;
                actions.push({
                    op: "create_value",
                    ref: v.id,
                    groupRef: grp.id,
                    displayName: value,
                    order: valueIndex,
                });
            });
            return;
        }

        // Existing group — rename and/or retype?
        const seedName = (seedGroup.displayName || "").trim();
        const groupPatch = {};
        if (name && name !== seedName) groupPatch.displayName = name;
        if (type !== seedGroup.type) groupPatch.type = type;
        if (Object.keys(groupPatch).length) {
            actions.push({ op: "update_group", groupId: grp.id, ...groupPatch });
        }

        const seedValueIds = new Set(seedGroup.values.map((v) => v.id));
        const seedValueById = new Map(seedGroup.values.map((v) => [v.id, v]));
        const currentValueIds = new Set(grp.values.map((v) => v.id));

        grp.values.forEach((v, valueIndex) => {
            const value = (v.displayName || "").trim();
            if (!seedValueIds.has(v.id)) {
                // new value in an existing group
                if (value) {
                    actions.push({
                        op: "create_value",
                        ref: v.id,
                        groupId: grp.id,
                        displayName: value,
                        order: valueIndex,
                    });
                }
                return;
            }
            // existing value — rename?
            const seedValue = (seedValueById.get(v.id).displayName || "").trim();
            if (value && value !== seedValue) {
                actions.push({ op: "update_value", valueId: v.id, displayName: value });
            }
        });

        // values removed from this existing group
        seedGroup.values.forEach((v) => {
            if (!currentValueIds.has(v.id)) actions.push({ op: "delete_value", valueId: v.id });
        });
    });

    return actions;
};

export default ManageFiltersPanel;
